import { useCallback, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { auditReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, CountChip, Pager, Dash, errMessage, DEFAULT_PAGE_SIZE } from './ReportCentreKit';
import type { DeletedCancelledFilter, DeletedCancelledReportDto, DeletedCancelledRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'DELETED_CANCELLED';

const CATEGORY_OPTIONS = [
  { value: '',          label: 'All Categories' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'DELETED',   label: 'Deleted' },
];

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Category', header: 'Category',     width: 12 },
  { key: 'Journal',  header: 'Journal No',   width: 18 },
  { key: 'Date',     header: 'Doc Date',     width: 14 },
  { key: 'Type',     header: 'Type',         width: 12 },
  { key: 'Debit',    header: 'Total Debit',  width: 16, numFmt: '#,##0.00' },
  { key: 'By',       header: 'Action By',    width: 22 },
  { key: 'At',       header: 'Action Date',  width: 20 },
  { key: 'Reason',   header: 'Reason',       width: 46 },
];

function buildExportRows(rows: DeletedCancelledRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Category: r.category,
    Journal:  r.documentNumber,
    Date:     r.documentDate,
    Type:     r.transactionTypeCode,
    Debit:    r.totalDebit,
    By:       r.actionByName ?? '',
    At:       r.actionDate ?? '',
    Reason:   r.reason ?? '',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const DeletedCancelledPage = () => {
  const { hasPermission } = usePermission();

  const [filter, setFilter] = useState<DeletedCancelledFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [applied, setApplied] = useState<DeletedCancelledFilter | null>(null);
  const [data, setData] = useState<DeletedCancelledReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (base: DeletedCancelledFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setLoading(true);
    setError(null);
    try {
      const res = await auditReportApi.getDeletedCancelled(query);
      setData(res.data?.data ?? null);
      setApplied(query);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const run = useCallback(() => { void fetchPage(filter, 1); }, [fetchPage, filter]);

  if (!hasPermission(Permissions.RPT_AUDIT_TRAIL)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const page = data?.rows;
  const items = page?.items ?? [];
  const counts = data?.categoryCounts;

  const handleExcel = () => {
    if (!data || !page) return;
    exportToExcel({
      sheetName: 'Deleted & Cancelled',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(items),
      title: 'Deleted & Cancelled Register',
      subtitle: `${data.fromDate} to ${data.toDate} · Page ${page.pageNumber} of ${page.totalPages}`,
    }, `DeletedCancelled_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  const handleCsv = () => {
    if (!data || !page) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(items), `DeletedCancelled_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trash2 size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Deleted &amp; Cancelled Register</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={items.length}
          filterSummary={`${filter.fromDate}..${filter.toDate}${filter.category ? ` cat=${filter.category}` : ''}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <label className={LABEL_CLS}>From Date *</label>
              <input type="date" value={filter.fromDate} onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
            </div>
            <div className="w-44">
              <label className={LABEL_CLS}>To Date *</label>
              <input type="date" value={filter.toDate} onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
            </div>
            <div className="w-44">
              <label className={LABEL_CLS}>Category</label>
              <select
                value={filter.category ?? ''}
                onChange={e => setFilter(f => ({ ...f, category: e.target.value || undefined }))}
                className={INPUT_CLS}
              >
                {CATEGORY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <RunReportButton onClick={run} isLoading={loading} disabled={!filter.fromDate || !filter.toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to list cancelled and soft-deleted journals.' />
      )}

      {data && page && counts && (
        <div className="flex flex-col gap-4">
          {/* Category chips */}
          <div className="flex flex-wrap items-center gap-2">
            <CountChip label="Cancelled" count={counts.cancelled} tone="amber" />
            <CountChip label="Deleted" count={counts.deleted} tone="red" />
            <CountChip label="Reversed" count={counts.reversed} tone="purple" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Reversed journals are counted only — reversals live in the Reversal Register.
            </span>
          </div>

          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Journals</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              </span>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1100px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-28')}>Category</th>
                    <th className={cn(TH_CLS, 'w-40')}>Journal No</th>
                    <th className={cn(TH_CLS, 'w-28')}>Doc Date</th>
                    <th className={cn(TH_CLS, 'w-20')}>Type</th>
                    <th className={TH_R_CLS}>Total Debit</th>
                    <th className={cn(TH_CLS, 'w-44')}>Action By</th>
                    <th className={cn(TH_CLS, 'w-40')}>Action Date</th>
                    <th className={TH_CLS}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No cancelled or deleted journals in the selected window.
                      </td>
                    </tr>
                  ) : items.map(r => (
                    <tr key={`${r.category}-${r.journalId}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2">
                        <Pill label={r.category} tone={r.category === 'DELETED' ? 'red' : 'amber'} />
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.documentNumber}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.documentDate)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.transactionTypeCode}</td>
                      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalDebit)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.actionByName ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(r.actionDate)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                        {r.reason ?? (r.category === 'DELETED' ? <span className="text-gray-400 dark:text-gray-600 italic">not recorded for soft deletes</span> : <Dash />)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              pageNumber={page.pageNumber}
              totalPages={page.totalPages}
              totalCount={page.totalCount}
              shownCount={items.length}
              loading={loading}
              onPage={p => { void fetchPage(applied ?? filter, p); }}
              noun="journals"
            />
          </div>
        </div>
      )}
    </div>
  );
};
